import React, { useCallback, useEffect, useMemo, useRef, useState, PropsWithChildren, Dispatch, SetStateAction, useContext, MutableRefObject } from 'react';
import { Form, FormWith } from '../components/FormComponentTypes';
import groupBy from 'lodash/groupBy';
import { DataRemark } from '../api/DataRemarksApi';
import { makeFieldId } from '../util/getPathFromId';
import { extractFormFieldsFromSchema, FormField } from '../util/extractFormFieldsFromSchema';
import { UWEEntity } from '../api/types';
import useResourceLoader from '../util/useResourceLoader';
import UWEEntityApi from '../api/UWEEntityApi';
import { AuthContext } from './AuthContext';

/** Writes values into the form's data by path, the same way fields do through setFormDataValues. */
export type FormDataWriter = (values: Record<string, unknown>) => void;

/** Conflict-resolve state shared by every caller under one form (panels and fields), so their requests queue up. */
export type ConflictResolutionState = {
  pendingKeys: ReadonlySet<string>;
  setPendingKeys: Dispatch<SetStateAction<ReadonlySet<string>>>;
  queueRef: MutableRefObject<Promise<unknown>>;
};

type TUweFormContext = {
  currentEntity?: UWEEntity;
  setCurrentEntity: Dispatch<SetStateAction<UWEEntity | undefined>>;
  // Version-resolution map for the current page and every SubformField it references
  // (recursively), keyed by name — see UWEEntityApi.resolveFormVersions. SubformField reads this
  // instead of fetching itself by name, so subforms honor the same Draft/Scheduled/Active/Pinned
  // resolution as the top-level page instead of always showing whatever's currently live.
  resolvedFormVersions?: Record<string, { formId: number; version: number }>;
  pendingNotes: Array<Object>; // TODO type notes
  setPendingNotes: Dispatch<SetStateAction<Object[]>>;
  dataRemarks: Array<DataRemark>;
  setDataRemarks: Dispatch<SetStateAction<DataRemark[]>>;
  dataRemarksById: Record<string, DataRemark[]>;
  conflicts: Record<string, any>;
  setConflicts: Dispatch<SetStateAction<Record<string, any>>>;
  // Lets components outside the RJSF tree (the conflicts sidebar) write form data. FormComponent fills it in.
  formDataWriterRef: MutableRefObject<FormDataWriter | null>;
  conflictResolution: ConflictResolutionState;
  // Whether the mounted form is read-only, for sidebar panels outside it. FormComponent keeps it in sync.
  formReadOnly: boolean;
  setFormReadOnly: Dispatch<SetStateAction<boolean>>;
  subForms: Record<string, FormWith<{ path: string }>>;
  addSubformToContext: (subform: FormWith<{ path: string }>) => void;
  openTabs: Record<string, string>; // { [$id of parent]: $id }
  setOpenTabs: Dispatch<SetStateAction<Record<string, string>>>;
  availableTabs: Record<string, string[]>;
  setAvailableTabs: Dispatch<SetStateAction<Record<string, string[]>>>;
  navigateToField: (fieldId: string, highlightColor?: string) => void;
  formFields: FormField[];
  formFieldsByPath: Record<string, FormField>;
  formFieldsByDataPath: Record<string, FormField>;
  formFieldsById: Record<string, FormField>;
  showPrefix: boolean;
  showSuffix: boolean;
  showPercentDiff: boolean;
  setShowPrefix: Dispatch<SetStateAction<boolean>>;
  setShowSuffix: Dispatch<SetStateAction<boolean>>;
  setShowPercentDiff: Dispatch<SetStateAction<boolean>>;
  // OCIF-specific
  pastReports: UWEEntity[];
  loadingPastReports: boolean;
  errorLoadingPastReports: Error | undefined;
  loadingDataRemarks: boolean;
};

type UweFormContextProps = PropsWithChildren & {
  initialDataRemarks: DataRemark[];
  loadingDataRemarks?: boolean;
  form: Form;
  currentEntity?: UWEEntity;
  setCurrentEntity?: Dispatch<SetStateAction<UWEEntity | undefined>>;
  resolvedFormVersions?: Record<string, { formId: number; version: number }>;
};

const UWEFormContext = React.createContext<TUweFormContext | undefined>(undefined);

// Stable fallback reference for `initialDataRemarks` when the caller doesn't pass one (e.g. the
// "new entity" flow, which has no remarks yet). A `= []` default in the destructuring below would
// create a NEW array every render, and the effect that seeds `dataRemarks` from it depends on this
// value by reference — a fresh array every render means that effect (and the re-render it causes)
// never stops.
const EMPTY_DATA_REMARKS: DataRemark[] = [];

export function UWEFormContextProvider(props: UweFormContextProps) {
  const {
    children,
    initialDataRemarks = EMPTY_DATA_REMARKS,
    loadingDataRemarks = false,
    form,
    currentEntity,
    setCurrentEntity,
    resolvedFormVersions,
  } = props;
  const { value } = useUWEFormContextComponentHooks(
    form,
    initialDataRemarks,
    loadingDataRemarks,
    currentEntity,
    setCurrentEntity,
    resolvedFormVersions
  );

  return <UWEFormContext.Provider value={value}>{children}</UWEFormContext.Provider>;
}

function useUWEFormContextComponentHooks(
  form: Form,
  initialDataRemarks: DataRemark[],
  loadingDataRemarks: boolean = false,
  currentEntity?: UWEEntity,
  setCurrentEntity: Dispatch<SetStateAction<UWEEntity | undefined>> = () => {},
  resolvedFormVersions?: Record<string, { formId: number; version: number }>
): { value: TUweFormContext } {
  const auth = useContext(AuthContext);
  const orgId = auth?.user?.user?.actualOrganizationalUnitId;

  const [pendingNotes, setPendingNotes] = useState<Array<Object>>([]);
  const [dataRemarks, setDataRemarks] = useState<Array<DataRemark>>(Array.isArray(initialDataRemarks) ? initialDataRemarks : []);
  useEffect(() => {
    setDataRemarks(Array.isArray(initialDataRemarks) ? initialDataRemarks : []);
  }, [initialDataRemarks]);
  const [conflicts, setConflicts] = useState<Record<string, any>>({});
  const formDataWriterRef = useRef<FormDataWriter | null>(null);
  const [resolvingConflictKeys, setResolvingConflictKeys] = useState<ReadonlySet<string>>(() => new Set());
  const conflictResolveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  // Read-only until a form says otherwise, so panels never offer edits for a form that isn't there.
  const [formReadOnly, setFormReadOnly] = useState<boolean>(true);

  const [subForms, setSubForms] = useState<Record<string, FormWith<{ path: string }>>>({});

  const [availableTabs, setAvailableTabs] = useState<Record<string, string[]>>({});
  const [openTabs, setOpenTabs] = useState<Record<string, string>>({});

  const [showPrefix, setShowPrefix] = useState<boolean>(false);
  const [showSuffix, setShowSuffix] = useState<boolean>(false);
  const [showPercentDiff, setShowPercentDiff] = useState<boolean>(form?.uiSchema?.['showPercentDiffBtn'] ?? false);

  const addSubformToContext = useCallback(
    (sf: FormWith<{ path: string }>) => {
      setSubForms(prev => ({
        ...prev,
        [sf.name]: sf,
      }));
    },
    [setSubForms]
  );

  const navigateToField = useCallback(
    (fieldId: string, highlightColor: string = '') => {
      if (!fieldId) return;
      const tabs = Object.keys(openTabs);
      const tabToUpdate = tabs.find(tabKey => fieldId.includes(tabKey));
      if (tabToUpdate) {
        const activeTab = availableTabs[tabToUpdate].find(tabLeafId => fieldId.includes(`${tabLeafId}_`));
        if (!activeTab) return;
        setOpenTabs(prev => ({
          ...prev,
          [tabToUpdate]: activeTab,
        }));
      }

      // After form re-renders, scroll into view
      setTimeout(() => {
        const element = document.getElementById(fieldId);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (element.focus) element.focus();
          if (highlightColor) {
            element.classList.add(`${highlightColor}-highlighted`);
            setTimeout(() => {
              element.classList.remove(`${highlightColor}-highlighted`);
            }, 1500);
          }
        }
      }, 250);
    },
    [openTabs, setOpenTabs]
  );

  const formFields = useMemo(() => {
    if (!form || !form.schema) return [];
    const fields = extractFormFieldsFromSchema(form.schema, subForms, currentEntity);
    return fields;
  }, [form, subForms, currentEntity]);

  const { formFieldsByPath, formFieldsByDataPath, formFieldsById } = useMemo(() => {
    return formFields.reduce<{
      formFieldsById: Record<string, FormField>;
      formFieldsByPath: Record<string, FormField>;
      formFieldsByDataPath: Record<string, FormField>;
    }>(
      (result, field) => {
        result.formFieldsById[field.id] = field;
        result.formFieldsByPath[field.path] = field;
        result.formFieldsByDataPath[field.dataPath] = field;
        return result;
      },
      { formFieldsByPath: {}, formFieldsByDataPath: {}, formFieldsById: {} }
    );
  }, [formFields]);

  const dataRemarksById = useMemo(() => {
    const primaryPathOf = (dr: DataRemark) => (Array.isArray(dr.fieldPath) ? dr.fieldPath[0] : dr.fieldPath);
    // General remarks have no field, so there is nothing to key them by.
    return groupBy(dataRemarks.filter(primaryPathOf), dr => {
      const primaryPath = primaryPathOf(dr);
      if (formFieldsByDataPath[primaryPath] != null) {
        return formFieldsByDataPath[primaryPath].id;
      }
      if (formFieldsByPath[primaryPath] != null) {
        return formFieldsByPath[primaryPath].id;
      }
      return makeFieldId(primaryPath);
    });
  }, [dataRemarks, formFieldsByDataPath]);

  const [pastReports, loadingPastReports = true, errorLoadingPastReports] = useResourceLoader(async () => {
    if (!orgId) return [];

    if (!currentEntity) return [];

    // Just do this for report entities without getting object schemas by id :)
    if (currentEntity?.data?.reportingWindow == null) {
      return [];
    }

    try {
      const lastThreeReportsMetadata = await UWEEntityApi.getWorkTray({
        resource: `uwe-entities/Report/list/${orgId}`,
        offset: 0,
        size: 10,
        sortBy: 'metadata.dueDate',
        closedStatus: 'closed',
        'metadata-eq-reportingWindowPeriodType': currentEntity.metadata.reportingWindowPeriodType,
        'metadata-eq-institutionId': `${currentEntity.organizationalUnit.id}`,
      });

      if (lastThreeReportsMetadata?.items?.length === 0) return [];

      const items = [...lastThreeReportsMetadata.items.slice(0, 3)];

      const lastThreeReports = (
        await Promise.all(
          (items || []).map(async rpt => {
            try {
              const entityId = rpt.id;
              const wfAccessToken = await UWEEntityApi.createWfAccessTokenHash({
                workflowId: currentEntity.workflowId,
                uweEntityId: entityId,
                canView: true,
                canUpdate: false,
                canCreate: false,
              });
              return await UWEEntityApi.getUWEEntity({ entityId, wfAccessToken });
            } catch (e) {
              console.error(`Error fetching report with ID ${rpt.id}`, e);
              return null;
            }
          })
        )
      ).filter(r => r);

      return lastThreeReports as UWEEntity[];
    } catch (e) {
      console.error('Error loading open reporting windows...');
      throw e;
    }
  }, [orgId]);

  return {
    value: {
      currentEntity,
      setCurrentEntity,
      resolvedFormVersions,
      pendingNotes,
      setPendingNotes,
      dataRemarks,
      setDataRemarks,
      conflicts,
      setConflicts,
      formDataWriterRef,
      conflictResolution: { pendingKeys: resolvingConflictKeys, setPendingKeys: setResolvingConflictKeys, queueRef: conflictResolveQueueRef },
      formReadOnly,
      setFormReadOnly,
      subForms,
      addSubformToContext,
      openTabs,
      setOpenTabs,
      availableTabs,
      setAvailableTabs,
      navigateToField,
      dataRemarksById,
      formFields,
      formFieldsByPath,
      formFieldsByDataPath,
      formFieldsById,
      showPrefix,
      showSuffix,
      showPercentDiff,
      setShowPrefix,
      setShowSuffix,
      setShowPercentDiff,
      // OCIF-specific
      pastReports: pastReports ?? [],
      loadingPastReports: loadingPastReports ?? false,
      errorLoadingPastReports,
      loadingDataRemarks,
    },
  };
}

export default UWEFormContext;
